import type { ReactElement, ReactNode } from 'react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../primitives/Card.js';

export interface PageIntroCardProps {
  eyebrow?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  meta?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
}

/**
 * Hero canônico de toda página interna. Visualmente carrega 3 elementos
 * de identidade da marca KM Gestão:
 *   - **Barra dourada à esquerda** (`border-l-4 border-l-accent`) — toque
 *     pontual de marca; remete ao gold da logomarca sem invadir o resto
 *     do card.
 *   - **Eyebrow em gold** (`text-accent uppercase tracking-wider`) —
 *     pequeno texto de breadcrumb/seção que reforça identidade.
 *   - **Sombra média + borda fina** — destaca o hero do resto da página
 *     sem "saltar" demais.
 *
 * Não use cor primária (preto) no eyebrow nem no border-left — esses
 * são os 2 lugares onde o gold aparece na hierarquia das páginas, e
 * trocar por preto remove a identidade de marca.
 */
export function PageIntroCard({
  eyebrow,
  title,
  description,
  meta,
  actions,
  children,
}: PageIntroCardProps): ReactElement {
  return (
    <Card className="border-border border-l-4 border-l-accent bg-card shadow-lg shadow-foreground/5 dark:shadow-2xl dark:shadow-black/40">
      <CardHeader className="space-y-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="space-y-2">
            {eyebrow ? (
              <div className="text-xs font-semibold uppercase tracking-wider text-accent">
                {eyebrow}
              </div>
            ) : null}
            <div className="space-y-2">
              <CardTitle className="text-2xl font-semibold tracking-tight">{title}</CardTitle>
              {description ? (
                <CardDescription className="max-w-3xl text-base leading-7 text-muted-foreground">
                  {description}
                </CardDescription>
              ) : null}
            </div>
          </div>
          {actions ? <div className="flex shrink-0 flex-wrap gap-2">{actions}</div> : null}
        </div>
        {meta ? <div className="flex flex-wrap items-center gap-2">{meta}</div> : null}
      </CardHeader>
      {children ? <CardContent className="space-y-4 pt-0">{children}</CardContent> : null}
    </Card>
  );
}
